import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, RotateCcw, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/Bits";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const TYPE_LABEL = { sales_commission: "Sales commission", hq_installer: "HQ installer" };
const STATUS_TINT = {
  pending: "bg-amber-50 text-amber-700",
  received: "bg-emerald-50 text-emerald-700",
};

export default function Dues() {
  const { user } = useAuth();
  const isHqFinance = ["network_admin", "accountant"].includes(user.role);
  const [dues, setDues] = useState([]);
  const [studios, setStudios] = useState([]);
  const [filters, setFilters] = useState({ studio: "all", type: "all", status: "all" });

  const load = useCallback(() => {
    const params = {};
    if (filters.studio !== "all") params.studio_id = filters.studio;
    if (filters.type !== "all") params.type = filters.type;
    if (filters.status !== "all") params.status = filters.status;
    api.get("/dues", { params }).then(({ data }) => setDues(data));
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isHqFinance) api.get("/studios").then(({ data }) => setStudios(data));
  }, [isHqFinance]);

  const toggle = async (due, received) => {
    try {
      await api.post(`/dues/${due.id}/receive`, { received });
      toast.success(received ? "Marked received" : "Reverted to pending");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const outstanding = dues
    .filter((d) => d.status === "pending")
    .reduce((s, d) => s + (d.amount || 0), 0);
  const received = dues
    .filter((d) => d.status === "received")
    .reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div>
      <PageHead
        title="Studio → HQ Dues"
        subtitle={
          isHqFinance
            ? "What studios owe HQ for sales commission and HQ-installer usage"
            : "What your studio owes HQ"
        }
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2" data-testid="dues-summary">
        <Card className="border-brand-line shadow-none">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Outstanding</p>
            <p data-testid="dues-outstanding" className="mt-1 font-display text-2xl font-semibold text-amber-600">
              {money(outstanding)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-brand-line shadow-none">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Received</p>
            <p data-testid="dues-received" className="mt-1 font-display text-2xl font-semibold text-emerald-600">
              {money(received)}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {isHqFinance && (
          <Select value={filters.studio} onValueChange={(v) => setFilters((f) => ({ ...f, studio: v }))}>
            <SelectTrigger className="h-9 w-48" data-testid="dues-filter-studio">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All studios</SelectItem>
              {studios.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={filters.type} onValueChange={(v) => setFilters((f) => ({ ...f, type: v }))}>
          <SelectTrigger className="h-9 w-44" data-testid="dues-filter-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="sales_commission">Sales commission</SelectItem>
            <SelectItem value="hq_installer">HQ installer</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
          <SelectTrigger className="h-9 w-40" data-testid="dues-filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="received">Received</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="dues-table">
            <TableHeader>
              <TableRow>
                {isHqFinance && <TableHead>Studio</TableHead>}
                <TableHead>Customer</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Attributed to</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                {isHqFinance && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {dues.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isHqFinance ? 7 : 5} className="py-10 text-center text-slate-500">
                    No dues found.
                  </TableCell>
                </TableRow>
              )}
              {dues.map((d) => (
                <TableRow key={d.id} data-testid={`due-row-${d.id}`}>
                  {isHqFinance && <TableCell className="font-medium">{d.studio_name}</TableCell>}
                  <TableCell>{d.customer_name || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {TYPE_LABEL[d.type] || d.type}
                      {d.source === "hq_flagged" && (
                        <Badge className="border-0 bg-rose-50 text-[10px] text-rose-600">flagged</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {d.cs_person_name || d.installer_name || "—"}
                  </TableCell>
                  <TableCell className="text-right font-semibold">{money(d.amount)}</TableCell>
                  <TableCell>
                    <Badge className={`border-0 capitalize ${STATUS_TINT[d.status]}`} data-testid={`due-status-${d.id}`}>
                      {d.status}
                      {d.status === "received" && d.received_date ? ` · ${fmtDate(d.received_date)}` : ""}
                    </Badge>
                  </TableCell>
                  {isHqFinance && (
                    <TableCell className="text-right">
                      {d.status === "pending" ? (
                        <Button size="sm" data-testid={`receive-due-${d.id}`} onClick={() => toggle(d, true)}>
                          <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Mark received
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" data-testid={`unreceive-due-${d.id}`} onClick={() => toggle(d, false)}>
                          <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Undo
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
